import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Icon from '../components/atoms/Icon';
import InterviewContinuationBanner from '../components/InterviewContinuationBanner';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';
import useCredits from '../hooks/useCredits';
import CreditCostBadge from '../components/molecules/CreditCostBadge';
import InsufficientCreditsModal from '../components/molecules/InsufficientCreditsModal';

const PlacementJobDetailsPage = () => {
  const navigate = useNavigate();
  const { positionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [position, setPosition] = useState(null);
  const [company, setCompany] = useState(null);
  const [existingAssessment, setExistingAssessment] = useState(null);

  const session = SessionManager.getCurrentSession();
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;
  const { hasEnoughCredits, deductCreditsForService } = useCredits();
  const [showCreditModal, setShowCreditModal] = useState(false);

  useEffect(() => {
    loadPositionData();
  }, [positionId]);

  const loadPositionData = async () => {
    try {
      setLoading(true);
      
      const headers = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      
      const response = await fetch(
        `${apiUrl}/placement-training/positions/${positionId}`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        setPosition(data.position);
        setCompany(data.company);
      }

      // Check for existing assessment (only if logged in)
      if (session?.access_token) {
        const historyResponse = await fetch(
          `${apiUrl}/placement-training/assessments/candidate/history`,
          { headers }
        );

        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          const existing = historyData.assessments?.find(
            a => a.position_id === positionId && a.status === 'in_progress'
          );
          setExistingAssessment(existing);
        }
      }
    } catch (error) {
      console.error('Error loading position data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = async () => {
    if (!session) {
      navigate('/login');
      return;
    }

    // Check credits
    if (!hasEnoughCredits('placement_training')) {
      setShowCreditModal(true);
      return;
    }

    // Deduct credits
    const creditResult = await deductCreditsForService('placement_training');
    if (!creditResult.success) {
      setShowCreditModal(true);
      return;
    }

    try {
      setStarting(true);

      const response = await fetch(
        `${apiUrl}/placement-training/assessments/start`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ position_id: positionId })
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Navigate to first round (aptitude)
        navigate(`/placement-training/assessment/${data.assessment_id}/aptitude`);
      } else {
        alert('Failed to start assessment. Please try again.');
      }
    } catch (error) {
      console.error('Error starting assessment:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const getRoundIcon = (roundName) => {
    const name = roundName.toLowerCase();
    if (name.includes('aptitude')) return 'quiz';
    if (name.includes('coding')) return 'code';
    if (name.includes('technical')) return 'engineering';
    if (name.includes('hr')) return 'people';
    return 'circle';
  };

  const getRoundDuration = (round) => {
    const name = round.toLowerCase();
    if (name.includes('aptitude')) return `${position.aptitude_duration_minutes} minutes`;
    if (name.includes('coding')) {
      const count = position.coding_problems_count;
      const duration = position.coding_duration_per_problem;
      return `${count} problems × ${duration} min`;
    }
    if (name.includes('advanced')) {
      const count = position.advanced_coding_problems_count;
      const duration = position.advanced_coding_duration_per_problem;
      return `${count} problems × ${duration} min`;
    }
    if (name.includes('technical')) return `${position.tech_interview_duration_minutes} minutes`;
    if (name.includes('hr')) return `${position.hr_interview_duration_minutes} minutes`;
    return 'Varies';
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-gray-50">
      <InterviewContinuationBanner />
      
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <LandingHeader />
      </div>

      <div className="max-w-5xl mx-auto w-full p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        ) : !position ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Icon name="error_outline" size={48} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Position not found</p>
            <button
              onClick={() => navigate('/placement-training')}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Back to Companies
            </button>
          </div>
        ) : (
          <div>
            {/* Back Button */}
            <button
              onClick={() => navigate(`/placement-training/company/${position.company_id}`)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
            >
              <Icon name="arrow_back" size={20} />
              <span>Back to {position.company_name}</span>
            </button>

        {/* Resume Banner */}
        {existingAssessment && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="info" size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-blue-900 mb-1">Resume Assessment</div>
                <div className="text-sm text-blue-700 mb-3">
                  You have an in-progress assessment for this position. You can continue from where you left off.
                </div>
                <button
                  onClick={() => navigate(`/placement-training/assessment/${existingAssessment.id}/aptitude`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Continue Assessment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Position Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-6">
          <div className="flex items-start gap-6">
            {company?.company_logo_url && (
              <div className="w-20 h-20 bg-gray-50 rounded-lg flex items-center justify-center p-2 flex-shrink-0">
                <img
                  src={company.company_logo_url}
                  alt={company.company_name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {position.position_title}
              </h1>
              <div className="text-lg text-gray-600 mb-4">{position.company_name}</div>
              
              <div className="flex flex-wrap gap-2">
                {position.experience_level && (
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full">
                    {position.experience_level}
                  </span>
                )}
                {position.salary_range && (
                  <span className="px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full">
                    {position.salary_range}
                  </span>
                )}
                <span className="px-3 py-1 bg-purple-50 text-purple-700 text-sm font-medium rounded-full">
                  {position.passing_threshold_percentage}% to Pass
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {position.position_description && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">About the Role</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {position.position_description}
            </p>
          </div>
        )}

        {/* Skills Required */}
        {position.skills_required && position.skills_required.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Skills Required</h2>
            <div className="flex flex-wrap gap-2">
              {position.skills_required.map((skill, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-gray-100 text-gray-800 text-sm font-medium rounded-lg"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Assessment Rounds */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Assessment Rounds</h2>
          <p className="text-gray-600 mb-6">
            Complete all {position.total_rounds} rounds sequentially to earn your certificate
          </p>

          <div className="space-y-4">
            {position.round_details && position.round_details.map((round, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={getRoundIcon(round.name || round)} size={24} className="text-blue-600" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {round.name || round}
                    </h3>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                    <div className="flex items-center gap-1">
                      <Icon name="schedule" size={16} />
                      <span>{getRoundDuration(round.name || round)}</span>
                    </div>
                    {round.description && (
                      <span className="text-gray-500">{round.description}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <Icon name="people" size={24} className="text-gray-400" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{position.total_attempts || 0}</div>
                <div className="text-sm text-gray-600">Total Attempts</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <Icon name="verified" size={24} className="text-green-500" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{position.total_passed || 0}</div>
                <div className="text-sm text-gray-600">Certificates Issued</div>
              </div>
            </div>
          </div>
        </div>

            {/* Start Button */}
            <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-xl p-8 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="mb-3">
                    <CreditCostBadge serviceType="placement_training" serviceName="Placement Training" size="sm" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Ready to Start?</h3>
                  <p className="text-teal-100">
                    Complete all rounds with {position.passing_threshold_percentage}% or higher to earn your certificate
                  </p>
                </div>
                <button
                  onClick={handleStartAssessment}
                  disabled={starting}
                  className="px-8 py-4 bg-white text-teal-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {starting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-600"></div>
                      <span>Starting...</span>
                    </>
                  ) : (
                    <>
                      <span>Start Assessment</span>
                      <Icon name="arrow_forward" size={20} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Insufficient Credits Modal */}
      {showCreditModal && (
        <InsufficientCreditsModal
          isOpen={showCreditModal}
          onClose={() => setShowCreditModal(false)}
          serviceName="Placement Training"
          serviceType="placement_training"
        />
      )}
    </div>
  );
};

export default PlacementJobDetailsPage;

